import { useMemo, useState } from "react";
import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import {
  formatWeight,
  type Measurement,
  type Point,
  type Sex,
  type UnitsPreference,
  formatDayMonth,
} from "@heraion/shared";

import { useDateLocale } from "../../primitives/DateFormatContext";
import { ChartCappedNote } from "../../charts/ChartCappedNote";
import type { UiTheme } from "../../theme";
import { makeSettingsCardStyles } from "../settingsCardStyles";
import { ChipStrip, type ChipOption } from "./ChipStrip";
import { compositionSeries } from "./composition";
import { MeasurementEmptyRow } from "./MeasurementEmptyRow";
import { MeasurementTrendChart, type MeasurementSeries } from "./MeasurementTrendChart";
import { InfoPill, InfoPillBody } from "../../primitives/InfoPill";
import { weightUnitLabels } from "../../weightUnitLabels";

// Measurement trends — the top of the Measurements page. A ChipStrip
// (multi-select) picks which metrics to chart; the default "Composition"
// chip shows weight + body-fat% + lean-mass as three trend lines, and each
// circumference with ≥1 data point gets its own selectable chip. A series
// with fewer than 2 points shows a short row instead of a chart (#151).
//
// Lifted to @heraion/ui (S3, 2026-06-29): the chart is now the shared,
// interactive WagmiLineChart (neutral series + an orange "now" dot, read by a
// tap since PG-64 fetta 3, and a CURRENT value for EVERY series; renderer
// swapped from our own SVG PG-1, 2026-09-17). #290: the dashed Health Connect
// curves of body fat and lean mass are gone, a scale's estimate being too far
// off; stored values stay, unshown. theme +
// units + locale come in as props so the web console renders the same trends.
// Reduced-vision: one chart area, large type, ≥44pt chips, anti-clutter.

const COMPOSITION = "composition";

// Title keys of the three composition series, for the row of one that has no
// points (no series object exists then).
const COMPOSITION_TITLES: Record<string, { key: string; en: string }> = {
  weight: { key: "mobile.body.weight", en: "Weight" },
  fat: { key: "mobile.body.metricBodyFat", en: "Body fat" },
  lean: { key: "mobile.body.metricLeanMass", en: "Lean mass" },
};

// Circumferences (head-to-toe) — weight + body-fat + lean live in the
// Composition group, height is effectively constant, so neither gets a
// standalone chip here.
const CIRC_FIELDS = [
  "neckCm",
  "chestCm",
  "waistCm",
  "hipsCm",
  "bicepsLCm",
  "bicepsRCm",
  "forearmsLCm",
  "forearmsRCm",
  "thighsLCm",
  "thighsRCm",
  "calvesLCm",
  "calvesRCm",
] as const;

type Series = MeasurementSeries;

type Props = {
  items: Measurement[];
  sex: Sex | undefined;
  units: UnitsPreference;
  locale: string;
  theme: UiTheme;
  /** Clock, injected: the trends date a series that has gone quiet, and a
   *  render-time Date.now() would make that untestable (HM-1). */
  nowMs: number;
  /** The measurements list goes further than the pages loaded: say the
   *  charts hold only the most recent data. */
  hasMore?: boolean;
  /** The card's heading, «TRENDS» by default. Home names the widget with it. */
  label?: string;
};

export function MeasurementTrends({
  items,
  sex,
  units,
  locale,
  theme,
  nowMs,
  hasMore,
  label,
}: Props) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  // The translated unit word for every weight below (CN-26 ondata 2).
  // ⛔ MEMOISED on `t`, not rebuilt per render: the object below feeds a
  // `useMemo` dependency array, and a fresh identity every render would turn
  // that memo into a no-op. `t` changes when the language does, which is
  // exactly when these labels must change.
  const unitLabels = useMemo(() => weightUnitLabels(t), [t]);
  const card = useMemo(() => makeSettingsCardStyles(theme), [theme]);

  const [selected, setSelected] = useState<string[]>([COMPOSITION]);
  const [expanded, setExpanded] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // Oldest→newest for charts; the list is newest-first from the API.
  const chrono = useMemo(() => [...items].reverse(), [items]);

  // All series, keyed; only those with ≥2 points are chartable.
  const series = useMemo(() => {
    const pt = (takenAt: string, y: number): Point => ({
      x: Date.parse(takenAt),
      y,
      label: formatDayMonth(new Date(takenAt), dateLocale, nowMs),
    });
    const out: Record<string, Series> = {};
    const fmtKg = (n: number) => formatWeight(n, units, locale, unitLabels);
    // Lean mass is a BF%-derived estimate: show whole units, not the 2-dp
    // precision of a logged weight (false precision, inbox 65d1a6ed).
    const fmtKgWhole = (n: number) =>
      formatWeight(n, units, locale, unitLabels, 0);
    const fmtCm = (n: number) => `${n} cm`;
    const fmtPct = (n: number) => `${n.toFixed(1)}%`;

    // Weight points stay in canonical kg; fmtKg (formatWeight) converts to
    // the user's unit for display — same as lean mass.
    const weight = chrono
      .filter((m) => m.weightKg !== undefined)
      .map((m) => pt(m.takenAt, m.weightKg as number));
    if (weight.length)
      out.weight = {
        key: "weight",
        title: t("mobile.body.weight", { defaultValue: "Weight" }),
        points: weight,
        yFormat: fmtKg,
      };

    // Body composition (the default, most-important trends). Navy BF% needs
    // height + circumferences (NOT weight); lean mass = weight × (1 − BF%)
    // moves on every weigh-in. compositionSeries carries each input forward
    // PER FIELD so a sparse tape day still recomputes BF% without freezing,
    // and never persists a derived value ([[feedback_no_denormalized_display_snapshots]]).
    const { fat: fatRaw, lean: leanRaw } = compositionSeries(chrono, sex);
    const fat = fatRaw.map((p) => pt(p.takenAt, p.value));
    const lean = leanRaw.map((p) => pt(p.takenAt, p.value));
    if (fat.length)
      out.fat = {
        key: "fat",
        title: t("mobile.body.metricBodyFat", { defaultValue: "Body fat" }),
        points: fat,
        yFormat: fmtPct,
      };
    if (lean.length)
      out.lean = {
        key: "lean",
        title: t("mobile.body.metricLeanMass", { defaultValue: "Lean mass" }),
        points: lean,
        yFormat: fmtKgWhole,
      };

    for (const f of CIRC_FIELDS) {
      const pts = chrono
        .filter((m) => typeof m[f] === "number")
        .map((m) => pt(m.takenAt, m[f] as number));
      if (pts.length)
        out[f] = {
          key: f,
          title: t(`mobile.body.field_${f}`, { defaultValue: f }),
          points: pts,
          yFormat: fmtCm,
        };
    }
    return out;
  }, [chrono, sex, units, locale, dateLocale, t, unitLabels, nowMs]);

  // Roster chips: Composition always, then any circumference with ≥1 point.
  const options = useMemo<ChipOption[]>(() => {
    const opts: ChipOption[] = [
      {
        key: COMPOSITION,
        label: t("mobile.body.metricComposition", {
          defaultValue: "Composition",
        }),
      },
    ];
    for (const f of CIRC_FIELDS) {
      const sf = series[f];
      if (sf && sf.points.length >= 1) opts.push({ key: f, label: sf.title });
    }
    return opts;
  }, [series, t]);

  // Selected keys → the rows to show (Composition fans out to weight + fat +
  // lean), de-duped. Each is a chart (≥2 points) or a short row (0 or 1).
  const shown = useMemo(() => {
    const keys: string[] = [];
    for (const sel of selected) {
      if (sel === COMPOSITION) keys.push("weight", "fat", "lean");
      else keys.push(sel);
    }
    const seen = new Set<string>();
    const result: Array<{ key: string; title: string; series?: Series }> = [];
    for (const k of keys) {
      if (seen.has(k)) continue;
      seen.add(k);
      const s = series[k];
      const tk = COMPOSITION_TITLES[k];
      const title =
        s?.title ??
        (tk
          ? t(tk.key, { defaultValue: tk.en })
          : t(`mobile.body.field_${k}`, { defaultValue: k }));
      result.push({ key: k, title, series: s });
    }
    return result;
  }, [selected, series, t]);
  const anyChart = shown.some((r) => (r.series?.points.length ?? 0) >= 2);
  // One condition for the `?` and its body. The title here is the ChipStrip
  // label, so PanelTitleRow does not fit; the pill rides in its header.
  const helpVisible = anyChart && selected.length > 0;

  const toggle = (key: string) =>
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );

  return (
    <View style={card.card}>
      <ChipStrip
        label={label ?? t("mobile.body.trendsLabel", { defaultValue: "TRENDS" })}
        // A custom label (Home) is longer; the chips already invite selection.
        hint={
          label
            ? undefined
            : t("mobile.body.trendsHint", { defaultValue: "select metrics" })
        }
        expanded={expanded}
        onToggleExpanded={() => setExpanded((v) => !v)}
        expandA11y={t("mobile.body.trendsExpandA11y", {
          defaultValue: "Show all metrics",
        })}
        collapseA11y={t("mobile.body.trendsCollapseA11y", {
          defaultValue: "Collapse metrics",
        })}
        theme={theme}
        headerRight={
          helpVisible ? (
            <InfoPill
              theme={theme}
              open={helpOpen}
              onToggle={() => setHelpOpen((v) => !v)}
            />
          ) : null
        }
        options={options}
        mode="multi"
        selectedKeys={selected}
        onSelect={toggle}
      />

      {helpOpen && helpVisible ? (
        <InfoPillBody
          theme={theme}
          text={t("mobile.body.trendsHelp", {
            defaultValue:
              "One chart for each metric you select above. Tap a point on a chart to read its value and date.",
          })}
        />
      ) : null}

      {selected.length === 0 ? (
        <Text style={card.cardHelp}>
          {t("mobile.body.trendsPick", {
            defaultValue: "Select a measurement above to see its trend.",
          })}
        </Text>
      ) : (
        <>
          {shown.map((r) =>
            r.series && r.series.points.length >= 2 ? (
              <MeasurementTrendChart
                key={r.key}
                series={r.series}
                sex={sex}
                theme={theme}
                nowMs={nowMs}
              />
            ) : (
              <MeasurementEmptyRow
                key={r.key}
                title={r.title}
                lastLabel={r.series?.points[0]?.label}
                theme={theme}
              />
            ),
          )}
          {hasMore && anyChart ? <ChartCappedNote theme={theme} /> : null}
        </>
      )}
    </View>
  );
}
